import type { Locale } from '@/strings/types';

/* Cross-origin links into the app (app.prinotes.com).
 *
 * Log in MUST be locale-pinned. Bare /login is browser-detected, not English:
 * it boots i18next's LanguageDetector (localStorage, then navigator), so an
 * Arabic-locale visitor arriving from an English marketing page lands on an
 * Arabic login screen, and vice versa. /login/<lang> exists precisely to hand
 * the language across the origin boundary — the app applies it pre-paint in
 * pn-fouc.js, with a changeLanguage effect in Login.tsx as the fallback layer.
 *
 * Shipped in MoMP-Online c90ed305 (prod deploy 2026-07-27). The app route is
 * `/login/:lang` and its allowlist accepts only 'en' and 'ar'. */
const APP_ORIGIN = 'https://app.prinotes.com';

export function appLoginUrl(locale: Locale): string {
  return `${APP_ORIGIN}/login/${locale}`;
}

/* Signup resolves to the SAME login page, by design: there is no separate
 * signup process. Account creation happens through the Google SSO button on
 * the login screen, which signs in and registers with one action.
 *
 * /signup is not a route at all. The app registers /login, /login/:lang, and a
 * /* splat wrapped in ProtectedRoute — so /signup fell through to the splat and
 * an unauthenticated visitor got bounced to BARE /login, which re-introduced
 * the browser-detection bug plus a redirect hop.
 *
 * Kept as its own function rather than inlining appLoginUrl at each CTA so the
 * "Start free" intent stays legible, and so there is one place to change if a
 * real signup route ever ships. */
export function appSignupUrl(locale: Locale): string {
  return appLoginUrl(locale);
}

/* True for links into the app. Cross-origin, but NOT "external" in the sense
 * that matters for tab behaviour: prinotes.com -> app.prinotes.com is a
 * first-party navigation and should keep the user in their existing tab, the
 * same as TopNav, Drawer, and the Hero CTA already do.
 *
 * Lives here rather than as a per-link flag on the copy so the rule is applied
 * automatically instead of remembered. The footer opened Log in in a new tab
 * for exactly that reason: it set `external: true` (true, it is cross-origin)
 * and MaybeLink read that as "new tab", so the same action behaved differently
 * depending on where you clicked it. */
export function isAppUrl(href: string): boolean {
  return href.startsWith(`${APP_ORIGIN}/`) || href === APP_ORIGIN;
}
